import React, { useCallback, useEffect, useState } from "react";

import {
  Avatar,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Tooltip,
  makeStyles,
  TextField,
  Typography,
  Switch,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import ImageOutlinedIcon from "@material-ui/icons/ImageOutlined";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";

import { toast } from "react-toastify";

import api from "../../services/api";
import EditorDeImagem from "../EditorDeImagem";
import { getBackendUrl } from "../../config";
import toastError from "../../errors/toastError";

const LIMITE = 1000;

const useStyles = makeStyles((theme) => {
  const escuro = theme.palette.type === "dark";

  return {
    paper: {
      borderRadius: 18,
      overflow: "hidden",
      backgroundColor: theme.palette.background.paper,
    },
    header: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "18px 22px",
      borderBottom: `1px solid ${theme.palette.divider}`,
      background: escuro
        ? "linear-gradient(180deg, rgba(30,152,85,0.12) 0%, rgba(0,0,0,0) 100%)"
        : "linear-gradient(180deg, #f0fdf4 0%, #ffffff 100%)",
    },
    avatar: {
      width: 42,
      height: 42,
      fontWeight: 700,
      backgroundColor: escuro ? "rgba(30,152,85,0.18)" : "#e7f6ed",
      color: "#1E9855",
    },
    titulo: {
      fontWeight: 700,
      fontSize: "1.05rem",
      color: theme.palette.text.primary,
      lineHeight: 1.2,
    },
    subtitulo: {
      fontSize: "0.8rem",
      color: theme.palette.text.secondary,
    },
    fechar: {
      marginLeft: "auto",
      color: theme.palette.text.secondary,
      "&:hover": { color: theme.palette.text.primary },
    },
    conteudo: {
      padding: 22,
    },
    campo: {
      "& .MuiOutlinedInput-root": {
        borderRadius: 12,
        backgroundColor: escuro ? "rgba(255,255,255,0.03)" : "#fafafa",
        alignItems: "flex-start",
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: escuro ? "rgba(255,255,255,0.10)" : "#e5e7eb",
      },
      "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "#1E9855",
      },
      "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#1E9855 !important",
        borderWidth: 2,
      },
      "& .MuiInputLabel-outlined.Mui-focused": {
        color: "#1E9855",
        fontWeight: 600,
      },
    },

    areaImagem: {
      marginTop: 14,
      display: "flex",
      alignItems: "center",
      gap: 12,
      flexWrap: "wrap",
    },
    miniatura: {
      width: 62,
      height: 62,
      borderRadius: 10,
      objectFit: "cover",
      border: `1px solid ${theme.palette.divider}`,
      display: "block",
    },
    miniaturaBox: {
      position: "relative",
      width: 62,
      height: 62,
      borderRadius: 10,
      overflow: "hidden",
      cursor: "pointer",
      flexShrink: 0,

      "& $lapis": { opacity: 0 },
      "&:hover $lapis": { opacity: 1 },
    },
    lapis: {
      position: "absolute",
      inset: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "rgba(0,0,0,0.55)",
      color: "#fff",
      transition: "opacity 0.15s ease",
    },
    miniaturaVazia: {
      width: 62,
      height: 62,
      borderRadius: 10,
      border: `1px dashed ${theme.palette.divider}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: theme.palette.text.disabled,
    },
    botaoImagem: {
      textTransform: "none",
      borderRadius: 10,
      fontWeight: 600,
    },
    dicaImagem: {
      fontSize: "0.72rem",
      color: theme.palette.text.secondary,
      width: "100%",
    },

    previaTitulo: {
      fontSize: "0.7rem",
      fontWeight: 700,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: theme.palette.text.secondary,
      margin: "18px 0 8px",
    },
    previaTela: {
      borderRadius: 12,
      padding: 14,
      minHeight: 90,
      display: "flex",
      flexDirection: "column",
      justifyContent: "flex-end",
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: escuro ? "#131917" : "#e9f1e9",
      backgroundImage: escuro
        ? "radial-gradient(rgba(255,255,255,0.035) 1px, transparent 1px)"
        : "radial-gradient(rgba(0,0,0,0.045) 1px, transparent 1px)",
      backgroundSize: "14px 14px",
    },
    balao: {
      display: "flex",
      flexDirection: "column",
      alignSelf: "flex-end",
      maxWidth: "92%",
      backgroundColor: escuro ? "#075E54" : "#d9fdd3",
      color: escuro ? "#e9edef" : "#111b21",
      borderRadius: "12px 12px 2px 12px",
      padding: "9px 12px 6px",
      fontSize: "0.84rem",
      lineHeight: 1.45,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
    },
    balaoImagem: {
      width: "100%",
      minWidth: 260,
      height: "auto",
      maxHeight: 280,
      objectFit: "cover",
      borderRadius: 8,
      display: "block",
      marginBottom: 6,
    },
    balaoVazio: {
      alignSelf: "center",
      color: theme.palette.text.secondary,
      fontSize: "0.8rem",
      fontStyle: "italic",
      textAlign: "center",
    },
    balaoRodape: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: 4,
      marginTop: 2,
      fontSize: "0.66rem",
      opacity: 0.7,
    },
    toggleBox: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      marginTop: 16,
      padding: "6px 14px 6px 6px",
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 12,
      backgroundColor: escuro ? "rgba(255,255,255,0.02)" : "#fafafa",
    },
    toggleTexto: {
      fontSize: "0.83rem",
      color: theme.palette.text.secondary,
      lineHeight: 1.3,
    },
    acoes: {
      padding: "14px 22px 20px",
      borderTop: `1px solid ${theme.palette.divider}`,
      backgroundColor: escuro ? "rgba(255,255,255,0.01)" : "#fafafa",
      gap: 10,
    },
    cancelar: {
      borderRadius: 10,
      fontWeight: 600,
      textTransform: "none",
      padding: "8px 22px",
      color: theme.palette.text.secondary,
      borderColor: theme.palette.divider,
    },
    salvar: {
      borderRadius: 10,
      fontWeight: 700,
      textTransform: "none",
      padding: "8px 26px",
      backgroundColor: "#1E9855",
      color: "#fff",
      boxShadow: "0 4px 14px rgba(30,152,85,0.30)",
      "&:hover": { backgroundColor: "#17803f" },
    },
  };
});

const GreetingModal = ({ open, usuario, onClose, onSaved }) => {
  const classes = useStyles();

  const [mensagem, setMensagem] = useState("");
  const [ativa, setAtiva] = useState(true);
  const [salvando, setSalvando] = useState(false);

  const [cadastro, setCadastro] = useState(null);

  const [imagem, setImagem] = useState("");
  const [enviandoImagem, setEnviandoImagem] = useState(false);
  const [paraEditar, setParaEditar] = useState(null);
  const [carregandoEdicao, setCarregandoEdicao] = useState(false);
  const [imagemMudou, setImagemMudou] = useState(false);

  const idDoInput = `input-imagem-saudacao-${usuario?.id || "x"}`;

  useEffect(() => {
    if (!open || !usuario) return undefined;

    setMensagem(usuario.greetingMessage || "");
    setAtiva(usuario.sendGreetingOnAccept !== false);
    setImagem(usuario.greetingMediaUrl || "");
    setCadastro(null);
    setParaEditar(null);
    setImagemMudou(false);

    let ativo = true;

    (async () => {
      try {
        const { data } = await api.get(`/users/${usuario.id}`);
        if (!ativo) return;
        setCadastro(data);
        setMensagem(data.greetingMessage || "");
        setAtiva(data.sendGreetingOnAccept !== false);
        setImagem(data.greetingMediaUrl || "");
      } catch (err) {
        if (ativo) toastError(err);
      }
    })();

    return () => {
      ativo = false;
    };
  }, [open, usuario]);

  const urlDaImagem = imagem ? `${getBackendUrl()}public/${imagem}` : "";



  const fechar = useCallback(() => {
    if (imagemMudou && onSaved) onSaved();
    onClose();
  }, [imagemMudou, onSaved, onClose]);

  const escolherImagem = (e) => {
    const arquivo = e.target.files?.[0];
    e.target.value = "";
    if (!arquivo) return;

    if (!/^image\/(jpe?g|png|webp)$/i.test(arquivo.type)) {
      toast.error("Use uma imagem JPG, PNG ou WEBP.");
      return;
    }

    if (arquivo.size > 15 * 1024 * 1024) {
      toast.error("A imagem precisa ter no máximo 15 MB.");
      return;
    }

    setParaEditar(arquivo);
  };

  const editarImagemAtual = async () => {
    if (!imagem || carregandoEdicao) return;

    setCarregandoEdicao(true);
    try {
      const { data, headers } = await api.get(`/public/${imagem}`, {
        responseType: "blob",
      });

      const tipo = headers["content-type"] || "image/jpeg";
      setParaEditar(new File([data], imagem, { type: tipo }));
    } catch (err) {
      toastError(err);
    } finally {
      setCarregandoEdicao(false);
    }
  };

  const enviarImagem = async (arquivoFinal) => {
    if (!usuario) return;

    setParaEditar(null);
    setEnviandoImagem(true);
    try {
      const corpo = new FormData();
      corpo.append("greetingImage", arquivoFinal);

      const { data } = await api.post(
        `/users/${usuario.id}/greeting/image`,
        corpo,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      setImagem(data.greetingMediaUrl || "");
      setImagemMudou(true);
      toast.success(`Imagem da saudação de ${usuario.name} atualizada!`);
    } catch (err) {
      toastError(err);
    } finally {
      setEnviandoImagem(false);
    }
  };

  const removerImagem = async () => {
    if (!usuario) return;

    setEnviandoImagem(true);
    try {
      const { data } = await api.delete(`/users/${usuario.id}/greeting/image`);
      setImagem(data.greetingMediaUrl || "");
      setImagemMudou(true);
      toast.success("Imagem removida.");
    } catch (err) {
      toastError(err);
    } finally {
      setEnviandoImagem(false);
    }
  };

  const salvar = async () => {
    if (!usuario) return;
    setSalvando(true);
    try {
      const base = cadastro || usuario;
      await api.put(`/users/${usuario.id}`, {
        name: base.name,
        email: base.email,
        profile: base.profile,
        whatsappId: base.whatsappId || base.whatsapp?.id || null,
        queueIds: (base.queues || []).map((q) => q.id),
        greetingMessage: mensagem,
        sendGreetingOnAccept: ativa,
      });
      toast.success(`Saudação de ${usuario.name} salva!`);
      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSalvando(false);
    }
  };

  const agora = new Date().toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  });

  const avatarSrc = usuario?.profileImage
    ? `${getBackendUrl()}public/${usuario.profileImage}`
    : undefined;

  return (
    <>
      <Dialog
        open={open}
        onClose={fechar}
        maxWidth="sm"
        fullWidth
        PaperProps={{ className: classes.paper }}
      >
        <div className={classes.header}>
          <Avatar
            className={classes.avatar}
            src={avatarSrc}
            imgProps={{ loading: "lazy", decoding: "async" }}
          >
            {usuario?.name ? usuario.name.charAt(0).toUpperCase() : "?"}
          </Avatar>
          <div style={{ minWidth: 0 }}>
            <Typography className={classes.titulo} noWrap>
              Saudação de {usuario?.name || ""}
            </Typography>
            <Typography className={classes.subtitulo} noWrap>
              {usuario?.email}
            </Typography>
          </div>
          <IconButton size="small" className={classes.fechar} onClick={fechar}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        <DialogContent className={classes.conteudo}>
          <TextField
            className={classes.campo}
            label="Mensagem de apresentação"
            placeholder={`Olá! Tudo bem? Me chamo ${
              usuario?.name || "..."
            } e vou dar continuidade ao seu atendimento 😊`}
            variant="outlined"
            fullWidth
            multiline
            rows={6}
            autoFocus
            value={mensagem}
            onChange={(e) => setMensagem(e.target.value.slice(0, LIMITE))}
            helperText={`${mensagem.length}/${LIMITE} caracteres · em branco não envia nada`}
          />

          {}
          <div className={classes.areaImagem}>
            <input
              id={idDoInput}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              style={{ display: "none" }}
              onChange={escolherImagem}
            />

            {urlDaImagem ? (
              <Tooltip title="Editar enquadramento">
                <div
                  className={classes.miniaturaBox}
                  onClick={editarImagemAtual}
                  role="button"
                  tabIndex={0}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") editarImagemAtual();
                  }}
                >
                  <img
                    className={classes.miniatura}
                    src={urlDaImagem}
                    alt="Imagem da saudação"
                    loading="lazy"
                    decoding="async"
                  />
                  <span className={classes.lapis}>
                    {carregandoEdicao ? (
                      <CircularProgress size={18} style={{ color: "#fff" }} />
                    ) : (
                      <EditOutlinedIcon fontSize="small" />
                    )}
                  </span>
                </div>
              </Tooltip>
            ) : (
              <label
                htmlFor={idDoInput}
                className={classes.miniaturaVazia}
                style={{ cursor: "pointer" }}
              >
                <ImageOutlinedIcon />
              </label>
            )}

            <label htmlFor={idDoInput}>
              <Button
                component="span"
                variant="outlined"
                size="small"
                className={classes.botaoImagem}
                disabled={enviandoImagem}
                startIcon={
                  enviandoImagem ? (
                    <CircularProgress size={14} />
                  ) : (
                    <ImageOutlinedIcon fontSize="small" />
                  )
                }
              >
                {urlDaImagem ? "Trocar imagem" : "Adicionar imagem"}
              </Button>
            </label>

            {urlDaImagem && (
              <Button
                size="small"
                className={classes.botaoImagem}
                disabled={enviandoImagem}
                onClick={removerImagem}
                startIcon={<DeleteOutlineIcon fontSize="small" />}
              >
                Remover
              </Button>
            )}

            <span className={classes.dicaImagem}>
              Opcional. A imagem vai junto com a mensagem, como legenda. Dá para
              enquadrar, girar e ajustar o zoom antes de salvar. A imagem é
              gravada na hora — só o texto depende do botão Salvar.
            </span>
          </div>

          <div className={classes.toggleBox}>
            <Switch
              checked={ativa}
              onChange={(e) => setAtiva(e.target.checked)}
              color="primary"
              size="small"
            />
            <span className={classes.toggleTexto}>
              {ativa
                ? "Envio automático ligado (1ª transferência)"
                : "Envio automático desligado para este login (só pelo \"+\")"}
            </span>
          </div>

          <Typography className={classes.previaTitulo}>
            Prévia para o cliente
          </Typography>
          <div className={classes.previaTela}>
            {mensagem.trim() || urlDaImagem ? (
              <div className={classes.balao}>
                {urlDaImagem && (
                  <img
                    className={classes.balaoImagem}
                    src={urlDaImagem}
                    alt="Imagem da saudação"
                    loading="lazy"
                    decoding="async"
                  />
                )}
                {mensagem}
                <div className={classes.balaoRodape}>
                  <span>{agora}</span>
                  <DoneAllIcon style={{ fontSize: 13 }} />
                </div>
              </div>
            ) : (
              <span className={classes.balaoVazio}>
                Sem mensagem configurada.
              </span>
            )}
          </div>
        </DialogContent>

        <DialogActions className={classes.acoes}>
          <Button
            variant="outlined"
            className={classes.cancelar}
            onClick={fechar}
            disabled={salvando}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            className={classes.salvar}
            onClick={salvar}
            disabled={salvando}
          >
            {salvando ? "Salvando..." : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>

      <EditorDeImagem
        open={!!paraEditar}
        arquivo={paraEditar}
        onCancel={() => setParaEditar(null)}
        onConfirmar={enviarImagem}
      />
    </>
  );
};

export default GreetingModal;
